import { StepLayout } from "#/components/carrot-plans/shared/StepLayout"
import {
  ButtonFooterLayout,
  ContinueButton,
  RoutingFlowTitle,
  SkipButton,
  useConvertTranslatedLabel
} from "#/components/carrot-plans/shared"
import React from "react"
import { Form } from "@carrotfertility/carotene-core-x"
import { defineMessage, FormattedMessage, MessageDescriptor, useIntl } from "react-intl"
import { FormRadioButtonGroup } from "#/services/common-forms"
import { useGetMedicalInfo, useMedicalInfo } from "#/components/carrot-plans/context/MedicalInfoContext"
import { useStateMachine } from "#/components/context/stateMachine/StateMachineV2"

type BabyFeedingFieldName = "pregnancyBabyFeedingPlan" | "postpartumBabyFeedingStatus"

function BabyFeedingStep({
  fieldName,
  headerText,
  options
}: {
  fieldName: BabyFeedingFieldName
  headerText: MessageDescriptor
  options: { label: MessageDescriptor; value: string }[]
}): JSX.Element {
  const intl = useIntl()
  const { updateMedicalInfo, hasQuestionBeenSkipped } = useMedicalInfo()
  const medicalInfoQuery = useGetMedicalInfo()
  const defaultValue = medicalInfoQuery.data?.[fieldName]

  const { send } = useStateMachine(defaultValue || hasQuestionBeenSkipped(fieldName) ? "" : null)

  async function onClickContinue(formValues: Record<string, string>): Promise<void> {
    const value = formValues[fieldName]
    if (value != null) {
      await updateMedicalInfo({ [fieldName]: value })
      send("")
    } else {
      await onSkip()
    }
  }

  async function onSkip(): Promise<void> {
    await updateMedicalInfo({ skipFields: [fieldName] })
    send("")
  }

  const radioOptions = useConvertTranslatedLabel(options)
  const fieldLabelId = `${fieldName}-label`

  return (
    <>
      <RoutingFlowTitle title={intl.formatMessage(headerText)} />
      <StepLayout
        header={<span id={fieldLabelId}>{intl.formatMessage(headerText)}</span>}
        formElements={
          <Form
            defaultValues={{
              [fieldName]: defaultValue || ""
            }}
            onSubmit={onClickContinue}
          >
            <FormRadioButtonGroup name={fieldName} aria-labelledby={fieldLabelId} options={radioOptions} />
            <ButtonFooterLayout
              continueButton={
                <ContinueButton>
                  <FormattedMessage defaultMessage="Continue" />
                </ContinueButton>
              }
              skipButton={<SkipButton onClick={onSkip} />}
            />
          </Form>
        }
      />
    </>
  )
}

const pregnantBabyFeedingHeader = defineMessage({ defaultMessage: "How do you plan to feed your baby?" })

const pregnantBabyFeedingOptions = [
  { label: defineMessage({ defaultMessage: "Breastfeeding" }), value: "BREASTFEEDING" },
  { label: defineMessage({ defaultMessage: "Formula feeding" }), value: "FORMULA" },
  { label: defineMessage({ defaultMessage: "Both breastfeeding and formula feeding" }), value: "MIXED_FEEDING" },
  { label: defineMessage({ defaultMessage: "I'm unsure/haven't decided yet" }), value: "NOT_SURE" },
  { label: defineMessage({ defaultMessage: "Prefer not to answer" }), value: "PREFER_NOT_TO_ANSWER" }
]

export const PregnantBabyFeedingStep = (): JSX.Element => (
  <BabyFeedingStep
    fieldName="pregnancyBabyFeedingPlan"
    headerText={pregnantBabyFeedingHeader}
    options={pregnantBabyFeedingOptions}
  />
)

const postpartumBabyFeedingHeader = defineMessage({
  defaultMessage: "If you are parenting an infant, how are you feeding your baby?"
})

const postpartumBabyFeedingOptions = [
  { label: defineMessage({ defaultMessage: "Breastfeeding" }), value: "BREASTFEEDING" },
  { label: defineMessage({ defaultMessage: "Formula feeding" }), value: "FORMULA" },
  { label: defineMessage({ defaultMessage: "Both breastfeeding and formula feeding" }), value: "MIXED_FEEDING" },
  { label: defineMessage({ defaultMessage: "Prefer not to answer" }), value: "PREFER_NOT_TO_ANSWER" }
]

export const PostpartumBabyFeedingStep = (): JSX.Element => (
  <BabyFeedingStep
    fieldName="postpartumBabyFeedingStatus"
    headerText={postpartumBabyFeedingHeader}
    options={postpartumBabyFeedingOptions}
  />
)
